import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";

const apiCall = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

apiCall.interceptors.request.use(
  (config) => {
    if (config.skipAuth) return config;
    const token = typeof window !== "undefined" ? localStorage.getItem(AUTH_TOKEN) : null;
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiCall.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;
    const status = err?.response?.status;

    if (status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);

      if (!refreshToken) {
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem('auth_user');
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiCall(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
        const newToken = data.token || data.accessToken;
        const newRefreshToken = data.refreshToken;

        if (newToken) {
          localStorage.setItem(AUTH_TOKEN, newToken);
          if (newRefreshToken) localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
          if (data.user) localStorage.setItem('auth_user', JSON.stringify(data.user));

          processQueue(null, newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return apiCall(originalRequest);
        }
        processQueue(err);
        return Promise.reject(err);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem('auth_user');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default apiCall;
